import { useId } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers } from "../../../types/onboarding";
import styles from "./FertilityStatusQuestion.module.css";

interface FertilityStatusQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | number | string[]) => void;
	showValidation?: boolean;
}

// Icon components
const MoonIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

const AnchorIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<circle
			cx="12"
			cy="5"
			r="3"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
		<line
			x1="12"
			y1="8"
			x2="12"
			y2="21"
			stroke="currentColor"
			strokeWidth="2"
		/>
		<path
			d="M5 12H2C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12H19"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
	</svg>
);

const AwardIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<circle
			cx="12"
			cy="8"
			r="6"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
		<path
			d="M15 13L17 22L12 19L7 22L9 13"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

const GiftIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<rect
			x="3"
			y="8"
			width="18"
			height="4"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
		<rect
			x="5"
			y="12"
			width="14"
			height="9"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
		<line
			x1="12"
			y1="8"
			x2="12"
			y2="21"
			stroke="currentColor"
			strokeWidth="2"
		/>
		<path
			d="M9 8C9 6.343 10.343 5 12 5C13.657 5 15 6.343 15 8"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			fill="none"
		/>
	</svg>
);

const HeartIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

const SunIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<circle
			cx="12"
			cy="12"
			r="4"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
		<line
			x1="12"
			y1="1"
			x2="12"
			y2="3"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
		<line
			x1="12"
			y1="21"
			x2="12"
			y2="23"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
		<line
			x1="4.22"
			y1="4.22"
			x2="5.64"
			y2="5.64"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
		<line
			x1="18.36"
			y1="18.36"
			x2="19.78"
			y2="19.78"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
		<line
			x1="1"
			y1="12"
			x2="3"
			y2="12"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
		<line
			x1="21"
			y1="12"
			x2="23"
			y2="12"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
		<line
			x1="4.22"
			y1="19.78"
			x2="5.64"
			y2="18.36"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
		<line
			x1="18.36"
			y1="5.64"
			x2="19.78"
			y2="4.22"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
	</svg>
);

export function FertilityStatusQuestion({
	answers,
	onInputChange,
	showValidation = false,
}: FertilityStatusQuestionProps) {
	const { t, direction } = useClientTranslations("onboarding");
	const { logOnboarding } = useAnalytics();
	const groupId = useId();

	const handleStatusSelect = (status: string) => {
		onInputChange("fertilityStatus", status);
		logOnboarding("status_selected", {
			field_name: "fertilityStatus",
			selected_value: status,
		});
	};

	const statusHasError = showValidation && !answers.fertilityStatus;

	const statusOptions = [
		{
			value: "Trying to conceive",
			label: t("fertilityStatus.options.tryingToConceive"),
			icon: <MoonIcon />,
		},
		{
			value: "Preserving fertility",
			label: t("fertilityStatus.options.preservingFertility"),
			icon: <AnchorIcon />,
		},
		{
			value: "Egg or Sperm Donor",
			label: t("fertilityStatus.options.donor"),
			icon: <AwardIcon />,
		},
		{
			value: "Surrogacy Journey",
			label: t("fertilityStatus.options.surrogacy"),
			icon: <GiftIcon />,
		},
		{
			value: "Currently pregnant",
			label: t("fertilityStatus.options.pregnant"),
			icon: <HeartIcon />,
		},
		{
			value: "Early parenting",
			label: t("fertilityStatus.options.parenting"),
			icon: <SunIcon />,
		},
	];

	return (
		<div className={styles.container} dir={direction}>
			{/* biome-ignore lint/a11y/useSemanticElements: Using div with role="group" to maintain consistent styling with other components */}
			<div className={styles.optionsList} role="group" id={groupId}>
				{statusOptions.map((option) => (
					<button
						key={option.value}
						type="button"
						onClick={() => handleStatusSelect(option.value)}
						className={`${styles.optionCard} ${
							answers.fertilityStatus === option.value
								? styles.optionCardSelected
								: ""
						} ${statusHasError ? styles.optionCardError : ""}`}
					>
						<div className={styles.iconContainer}>{option.icon}</div>
						<span className={styles.optionLabel}>{option.label}</span>
					</button>
				))}
			</div>
		</div>
	);
}
